import Vue from 'vue'
import VueRouter from 'vue-router'
import VueMeta from 'vue-meta'

import store from '@/state/store'
import routes from './routes'

Vue.use(VueRouter)
Vue.use(VueMeta, {
  keyName: 'page',
})

const router = new VueRouter({
  routes,
  mode: 'history',
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  },
})

// Before each route evaluates...
router.beforeEach((routeTo, routeFrom, next) => {
  // 首先检查是否是OAuth回调（URL中包含uid和token参数）
  if (typeof window !== 'undefined' && window.location && routeTo.query) {
    const uid = routeTo.query.uid || (window.location.search ? new URLSearchParams(window.location.search).get('uid') : null);
    const token = routeTo.query.token || (window.location.search ? new URLSearchParams(window.location.search).get('token') : null);
    const error = routeTo.query.error || (window.location.search ? new URLSearchParams(window.location.search).get('error') : null);
    
    if (error) {
      // 如果有错误，清除URL参数并跳转到登录页面
      if (window.history && window.location) {
        window.history.replaceState({}, document.title, '/login');
      }
      return next({ name: 'login', query: { error } });
    }
    
    if (uid && token && typeof localStorage !== 'undefined') {
      // OAuth回调成功，保存用户信息到localStorage
      localStorage.setItem("user", uid);
      localStorage.setItem("access_token", token);
      
      // 清除URL参数
      if (window.history && window.location) {
        const cleanPath = routeTo.path || window.location.pathname;
        window.history.replaceState({}, document.title, cleanPath);
      }
      
      // 如果当前路由是登录页面或首页，跳转到首页
      if (routeTo.name === 'login' || routeTo.path === '/') {
        return next({ name: 'home' });
      }
      
      // 否则继续当前路由
      return next();
    }
  }
  
  if (process.env.VUE_APP_DEFAULT_AUTH === "firebase") {
    // Check if auth is required on this route
    // (including nested routes).
    const authRequired = routeTo.matched.some((route) => route.meta.authRequired)

    // If auth isn't required for the route, just continue.
    if (!authRequired) return next()

    // If auth is required and the user is logged in...
    if (store.getters['auth/loggedIn']) {
      // Validate the local user token...
      return store.dispatch('auth/validate').then((validUser) => {
        // Then continue if the token still represents a valid user,
        // otherwise redirect to login.
        validUser ? next() : redirectToLogin()
      })
    }

    // If auth is required and the user is NOT currently logged in,
    // redirect to login.
    redirectToLogin()

    // eslint-disable-next-line no-unused-vars
    // eslint-disable-next-line no-inner-declarations
    function redirectToLogin() {
      // Pass the original route to the login component
      next({ name: 'login', query: { redirectFrom: routeTo.fullPath } })
    }
  } else {
    const publicPages = ['/login', '/register', '/forgot-password'];
    const authpage = !publicPages.includes(routeTo.path);
    
    // 检查用户是否登录（同时检查 user 和 access_token）
    const loggeduser = localStorage.getItem('user');
    const accessToken = localStorage.getItem('access_token');
    const isLoggedIn = !!(loggeduser && accessToken);

    if (authpage && !isLoggedIn) {
      return next({ name: 'login', query: { redirectFrom: routeTo.fullPath } });
    }

    next();
  }
})

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args)
              reject(new Error('Redirected'))
            } else {
              resolve()
            }
          })
        } else {
          // Otherwise, continue resolving the route.
          resolve()
        }
      })
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return
  }

  // If we reach this point, continue resolving the route.
  next()
})

export default router
